import {
  AfterViewInit, Component, DestroyRef, inject, Injector,
  input, model, OnDestroy, output, TemplateRef, viewChild, ViewContainerRef
} from '@angular/core';
import {takeUntilDestroyed, toObservable} from '@angular/core/rxjs-interop';
import {Overlay, OverlayRef} from '@angular/cdk/overlay';
import {TemplatePortal} from '@angular/cdk/portal';
import {IconsComponent} from '../icons/icons.component';

@Component({
  selector: 'app-modal',
  imports: [IconsComponent],
  templateUrl: './modal.html',
  styleUrl: './modal.css',
})
export class Modal implements AfterViewInit, OnDestroy {
  isOpen = model.required<boolean>();
  size = input<'sm' | 'md' | 'lg' | 'xl' | '2xl'>('md');
  closable = input<boolean>(true);

  closeModal = output();
  closeRequested = output();

  private tpl = viewChild.required<TemplateRef<void>>('tpl');

  private overlay = inject(Overlay);
  private vcr = inject(ViewContainerRef);
  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private overlayRef: OverlayRef | null = null;

  ngAfterViewInit(): void {
    toObservable(this.isOpen, {injector: this.injector})
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(open => {
        if (open) {
          this.attachOverlay();
        } else {
          this.detachOverlay();
        }
      });
  }

  private attachOverlay(): void {
    if (!this.overlayRef) {
      // usePopover: false — CDK 21 defaults to true (Popover API top layer), but the
      // tour overlay uses z-index stacking to stay above modals; mixing the two
      // causes modals to escape the z-index hierarchy on second open.
      this.overlayRef = this.overlay.create({
        scrollStrategy: this.overlay.scrollStrategies.block(),
        usePopover: false,
      });
    }
    if (!this.overlayRef.hasAttached()) {
      this.overlayRef.attach(new TemplatePortal(this.tpl(), this.vcr));
    }
  }

  private detachOverlay(): void {
    this.overlayRef?.detach();
  }

  onModalClick(event: Event): void {
    event.stopPropagation();
  }

  close(): void {
    if (this.closable()) {
      this.isOpen.set(false);
      return this.closeModal.emit();
    }
    this.closeRequested.emit();
  }

  getSizeClass(): string {
    const sizeMap = {
      'sm': 'max-w-sm',
      'md': 'max-w-md',
      'lg': 'max-w-lg',
      'xl': 'max-w-xl',
      '2xl': 'max-w-2xl',
    };
    return sizeMap[this.size()];
  }

  ngOnDestroy(): void {
    this.overlayRef?.dispose();
  }
}
